import {
    LEARN_TASKS,
    promptPlainText,
    type SlottedPrompt,
} from '@apps/web/features/learn_phoebe';

export type GhostSlotKind = 'caregiver' | 'client' | 'shift';

export type GhostCompletion = {
    readonly taskId: string;
    // Grey text shown after the user's typed draft (visual only).
    readonly completion: string;
    // Text inserted when the user accepts, ending in '@' to open the picker.
    readonly insertion: string;
    readonly slotKind: GhostSlotKind;
};

type LeadEntry = {
    readonly taskId: string;
    // Canonical lead tokens, original casing (for building the remainder).
    readonly leadTokens: ReadonlyArray<string>;
    readonly leadTokensLower: ReadonlyArray<string>;
    readonly slotText: string;
    readonly slotKind: GhostSlotKind;
};

function slotKindFromText(slot: string): GhostSlotKind | null {
    if (slot.includes('caregiver')) {
        return 'caregiver';
    }
    if (slot.includes('client')) {
        return 'client';
    }
    if (slot.includes('shift')) {
        return 'shift';
    }
    return null;
}

// Lead phrases that immediately precede an `@`-slot, drawn from the same
// Learn Phoebe prompt templates the product already teaches. A ghost
// completion continues the lead and ends in the `@ pick a …` slot so
// accepting it opens the existing mention picker.
function buildLeadEntries(): ReadonlyArray<LeadEntry> {
    const entries: Array<LeadEntry> = [];
    for (const task of LEARN_TASKS) {
        const prompt: SlottedPrompt = task.prompt;
        const first = prompt[0];
        const second = prompt[1];
        if (
            first === undefined ||
            second === undefined ||
            !('text' in first) ||
            !('slot' in second)
        ) {
            continue;
        }
        const slotKind = slotKindFromText(second.slot);
        if (slotKind === null || first.text.trim().length === 0) {
            continue;
        }
        const leadTokens = first.text.trim().split(/\s+/u);
        entries.push({
            taskId: task.id,
            leadTokens,
            leadTokensLower: leadTokens.map((token) => token.toLowerCase()),
            slotText: second.slot,
            slotKind,
        });
    }
    return entries;
}

const LEAD_ENTRIES = buildLeadEntries();

// Below this the draft is too short to be a confident intent match.
const MIN_TYPED_LENGTH = 4;

/**
 * True when `typed` is within one edit (insert/delete/substitute or one
 * adjacent transposition) of `target`. Both must be ≥ 4 chars so short common
 * words never fuzzy-collide.
 */
function isCloseToken(typed: string, target: string): boolean {
    if (typed === target) {
        return true;
    }
    if (typed.length < 4 || target.length < 4) {
        return false;
    }
    if (Math.abs(typed.length - target.length) > 1) {
        return false;
    }
    if (typed.length === target.length) {
        let i = 0;
        while (i < typed.length && typed[i] === target[i]) {
            i++;
        }
        if (i === typed.length) {
            return true;
        }
        // One substitution, or one adjacent transposition ("fnid" → "find").
        return (
            typed.slice(i + 1) === target.slice(i + 1) ||
            (typed[i] === target[i + 1] &&
                typed[i + 1] === target[i] &&
                typed.slice(i + 2) === target.slice(i + 2))
        );
    }
    const [shorter, longer] =
        typed.length < target.length ? [typed, target] : [target, typed];
    let i = 0;
    while (i < shorter.length && shorter[i] === longer[i]) {
        i++;
    }
    return shorter.slice(i) === longer.slice(i + 1);
}

/**
 * Given the plain-text draft the user is typing, return a ghost completion
 * toward the nearest matching `@`-slot prompt, or null.
 *
 * Matching is fuzzy and position-independent: the draft's trailing tokens are
 * matched against a prefix of each lead phrase, so "hey can you find coverage
 * for" still completes toward "@ pick a shift". Fully typed tokens tolerate
 * one typo ("fnid" → "find"); the final token may be a partial prefix
 * ("cov" → "coverage"). Single-token matches must be exact or a partial
 * prefix — never a typo — so common words don't fuzzy-collide. Ties prefer
 * the lead with the least left to complete.
 */
export function matchGhostCompletion(draft: string): GhostCompletion | null {
    if (draft.includes('@') || draft.includes('\n')) {
        return null;
    }
    if (draft.trim().length < MIN_TYPED_LENGTH) {
        return null;
    }
    const endsWithSpace = /\s$/u.test(draft);
    const draftTokens = draft
        .trim()
        .split(/\s+/u)
        .map((token) => token.toLowerCase());

    let best: {
        readonly matchedChars: number;
        readonly ghost: GhostCompletion;
    } | null = null;

    for (const entry of LEAD_ENTRIES) {
        const leadLower = entry.leadTokensLower;
        const maxK = Math.min(leadLower.length, draftTokens.length);
        for (let k = maxK; k >= 1; k--) {
            const tail = draftTokens.slice(draftTokens.length - k);
            let matched = true;
            let usedTypo = false;
            let partialRest: string | null = null;
            for (let i = 0; i < k; i++) {
                const typed = tail[i] ?? '';
                const target = leadLower[i] ?? '';
                const isLastTyped = i === k - 1;
                if (
                    isLastTyped &&
                    !endsWithSpace &&
                    typed.length < target.length &&
                    target.startsWith(typed)
                ) {
                    // Partial prefix of the next lead token ("cov"…"erage").
                    partialRest = (entry.leadTokens[i] ?? '').slice(
                        typed.length,
                    );
                    continue;
                }
                if (typed === target) {
                    continue;
                }
                if (isCloseToken(typed, target)) {
                    usedTypo = true;
                    continue;
                }
                matched = false;
                break;
            }
            if (!matched) {
                continue;
            }
            // A lone fuzzy token is too weak a signal ("next" ≠ "text").
            if (k === 1 && usedTypo) {
                continue;
            }
            const matchedChars = tail.join(' ').length;
            if (matchedChars < MIN_TYPED_LENGTH) {
                continue;
            }
            const rest = entry.leadTokens.slice(k).join(' ');
            let remainder: string;
            if (partialRest !== null) {
                remainder = partialRest + (rest ? ` ${rest} ` : ' ');
            } else if (rest) {
                remainder = `${endsWithSpace ? '' : ' '}${rest} `;
            } else {
                remainder = endsWithSpace ? '' : ' ';
            }
            const ghost: GhostCompletion = {
                taskId: entry.taskId,
                completion: remainder + entry.slotText,
                insertion: remainder + '@',
                slotKind: entry.slotKind,
            };
            const isBetter =
                best === null ||
                matchedChars > best.matchedChars ||
                (matchedChars === best.matchedChars &&
                    ghost.completion.length < best.ghost.completion.length);
            if (isBetter) {
                best = { matchedChars, ghost };
            }
            break;
        }
    }
    return best?.ghost ?? null;
}

export type PromptTemplateHint = {
    readonly taskId: string;
    // Short lesson title, shown as the hint label.
    readonly title: string;
    // The full ready-made prompt to drop into the composer.
    readonly templateText: string;
    // Where the trigger keyword sits in the draft, so accepting the hint can
    // replace just the matched sentence instead of the whole draft.
    readonly matchIndex: number;
};

// Keyword → Learn Phoebe lesson. Only lessons whose prompt is a single text
// segment (no `@`-slot) are offered as ready-made prompts, so the inserted text
// never carries a literal "[@ pick a …]" placeholder. Keywords are matched on
// word boundaries anywhere in the draft, so "send me the outreach report"
// still surfaces the report prompt.
const TEMPLATE_KEYWORDS: ReadonlyArray<{
    readonly taskId: string;
    readonly keywords: ReadonlyArray<string>;
}> = [
    { taskId: 'report', keywords: ['report'] },
    { taskId: 'walkthrough', keywords: ['how do i', 'how to', 'walkthrough'] },
    {
        taskId: 'watch',
        keywords: [
            'check back',
            'keep an eye',
            'remind me',
            'let me know when',
        ],
    },
];

function isSingleTextPrompt(prompt: SlottedPrompt): boolean {
    return prompt.every((segment) => 'text' in segment);
}

/**
 * Find `keyword` in the lowercase draft, returning its index or -1. Multi-word
 * phrases match verbatim on word boundaries; single words also accept a
 * one-edit LENGTH-CHANGING variant ("reports" → "report") via a token
 * scan. Same-length fuzzies are rejected on purpose: one substitution or
 * transposition away from a keyword is usually a different real word
 * ("repost" → "report", "callous" → "callout"), and a keyword is the sole
 * trigger for a whole hint — too weak a signal for that class of match.
 */
function keywordMatchIndex(text: string, keyword: string): number {
    const exact = new RegExp(`\\b${keyword}\\b`, 'u').exec(text);
    if (exact !== null) {
        return exact.index;
    }
    if (keyword.includes(' ')) {
        return -1;
    }
    for (const token of text.matchAll(/[\p{L}\p{N}'’-]+/gu)) {
        if (
            token[0].length !== keyword.length &&
            isCloseToken(token[0], keyword)
        ) {
            return token.index;
        }
    }
    return -1;
}

/**
 * Given the plain-text draft, return a ready-made Learn Phoebe prompt to offer
 * when the draft mentions a distinctive keyword but isn't a clean ghost lead.
 * Returns null once the draft already contains the template (so the hint
 * disappears after the user inserts it).
 */
export function matchPromptTemplate(draft: string): PromptTemplateHint | null {
    if (draft.includes('@')) {
        return null;
    }
    const text = draft.toLowerCase();
    if (text.trim().length < MIN_TYPED_LENGTH) {
        return null;
    }
    for (const entry of TEMPLATE_KEYWORDS) {
        let matchIndex = -1;
        for (const keyword of entry.keywords) {
            matchIndex = keywordMatchIndex(text, keyword);
            if (matchIndex >= 0) {
                break;
            }
        }
        if (matchIndex < 0) {
            continue;
        }
        const task = LEARN_TASKS.find(
            (candidate) => candidate.id === entry.taskId,
        );
        if (task === undefined || !isSingleTextPrompt(task.prompt)) {
            continue;
        }
        const templateText = promptPlainText(task.prompt);
        if (text.includes(templateText.toLowerCase())) {
            return null;
        }
        return { taskId: task.id, title: task.title, templateText, matchIndex };
    }
    return null;
}

export type DemoCardHint = {
    // A Learn Phoebe task id whose scripted demo to play inline.
    readonly taskId: string;
    // Where the trigger keyword sits in the draft (see PromptTemplateHint).
    readonly matchIndex: number;
};

// Keyword → Learn Phoebe lesson to demo inline. Reserved for features that
// have no ghost lead (their prompt starts with a slot), so the ghost and the
// demo card never compete for the same phrase.
const DEMO_CARD_KEYWORDS: ReadonlyArray<{
    readonly taskId: string;
    readonly keywords: ReadonlyArray<string>;
}> = [
    {
        taskId: 'callout',
        keywords: ['callout', 'called out', 'call out', 'called off'],
    },
];

/**
 * Given the plain-text draft, return a Learn Phoebe lesson to demo inline when
 * the draft describes a feature that has no ghost lead (e.g. a callout).
 */
export function matchDemoCard(draft: string): DemoCardHint | null {
    if (draft.includes('@')) {
        return null;
    }
    const text = draft.toLowerCase();
    if (text.trim().length < MIN_TYPED_LENGTH) {
        return null;
    }
    for (const entry of DEMO_CARD_KEYWORDS) {
        let matchIndex = -1;
        for (const keyword of entry.keywords) {
            matchIndex = keywordMatchIndex(text, keyword);
            if (matchIndex >= 0) {
                break;
            }
        }
        if (matchIndex < 0) {
            continue;
        }
        const task = LEARN_TASKS.find(
            (candidate) => candidate.id === entry.taskId,
        );
        if (task !== undefined) {
            return { taskId: task.id, matchIndex };
        }
    }
    return null;
}

/**
 * Composer-ready seed text for a Learn Phoebe prompt: the text up to the first
 * `@`-slot plus a trailing `@` so the mention picker opens for that slot —
 * mirroring the ghost-accept flow. Never emits a literal "[@ pick a …]"
 * placeholder. A slot-free prompt seeds its full text.
 */
export function promptSeedText(prompt: SlottedPrompt): string {
    let lead = '';
    for (const segment of prompt) {
        if ('slot' in segment) {
            return `${lead}@`;
        }
        lead += segment.text;
    }
    return lead;
}

const SENTENCE_BOUNDARY = /[.!?\n]/u;

/**
 * Replace only the sentence containing the matched trigger with `template`,
 * preserving everything before and after — so accepting a hint on a long,
 * multi-sentence draft doesn't destroy the rest of the message. For a short
 * single-sentence draft this degenerates to replacing the whole draft.
 * Returns the new text plus the caret position at the end of the insertion.
 */
export function replaceSentenceWithTemplate(
    draft: string,
    matchIndex: number,
    template: string,
): { readonly text: string; readonly caretOffset: number } {
    const anchor = Math.min(Math.max(matchIndex, 0), draft.length);
    let start = 0;
    for (let i = anchor - 1; i >= 0; i--) {
        if (SENTENCE_BOUNDARY.test(draft[i] ?? '')) {
            start = i + 1;
            break;
        }
    }
    while (start < anchor && /\s/u.test(draft[start] ?? '')) {
        start++;
    }
    let end = draft.length;
    for (let i = anchor; i < draft.length; i++) {
        if (SENTENCE_BOUNDARY.test(draft[i] ?? '')) {
            end = i;
            break;
        }
    }
    return {
        text: draft.slice(0, start) + template + draft.slice(end),
        caretOffset: start + template.length,
    };
}

export type NameCatchMatch = {
    readonly type: 'caregiver' | 'client';
    readonly id: string;
    readonly displayName: string;
};

export type NameCatchHint = {
    // The typed-out name (to replace with a pill on accept).
    readonly candidate: string;
    // One match → "Use @Name"; two or more → a disambiguation pick list.
    readonly matches: ReadonlyArray<NameCatchMatch>;
};

type CaregiverLike = { readonly id: string; readonly name: string };
type ClientLike = {
    readonly id: string;
    readonly first_name: string;
    readonly last_name: string;
    readonly preferred_name?: string | null;
};

// Words that are never a person's name and commonly end a draft or precede one.
// Rough by design — a fixed word list, not intelligent matching.
const NON_NAME_WORDS = new Set([
    'the',
    'a',
    'an',
    'for',
    'to',
    'me',
    'my',
    'your',
    'of',
    'and',
    'is',
    'it',
    'on',
    'in',
    'at',
    'out',
    'off',
    'up',
    'with',
    'from',
    'about',
    'text',
    'call',
    'message',
    'send',
    'tell',
    'ask',
    'remind',
    'remember',
    'find',
    'sync',
    'check',
    'ping',
    'add',
    'who',
    'what',
    'can',
    'you',
    'please',
    'today',
    'tomorrow',
    'tonight',
    'now',
]);

// A lowercase trailing word only counts as a name candidate right after one of
// these person-directed words ("text maria", "coverage for maria"). Without
// this gate, nearly every prose word would fire an org-wide record lookup.
const PERSON_CONTEXT_WORDS = new Set([
    'ask',
    'call',
    'contact',
    'for',
    'message',
    'notify',
    'ping',
    'remind',
    'tell',
    'text',
]);

/**
 * Extract a name-like candidate from the end of the draft: the last word, plus
 * a preceding word only when both look like a capitalized first+last name. A
 * lowercase word qualifies only after a person-directed word (see
 * PERSON_CONTEXT_WORDS), or after "and"/a comma once the draft already
 * contains a mention pill; a single common word (verb/preposition) yields
 * null. Callers resolve the candidate against real records before showing
 * anything.
 */
export function extractNameCandidate(draft: string): string | null {
    if (draft.includes('@') || draft.includes('\n')) {
        return null;
    }
    const trimmed = draft.replace(/[\s.,;:!?]+$/u, '');
    if (trimmed.length < 3) {
        return null;
    }
    const words = trimmed.split(/\s+/u);
    const last = words[words.length - 1] ?? '';
    if (last.length < 3 || !/^\p{L}[\p{L}'’.-]*$/u.test(last)) {
        return null;
    }
    if (NON_NAME_WORDS.has(last.toLowerCase())) {
        return null;
    }
    const previous = words[words.length - 2];
    if (
        previous !== undefined &&
        /^\p{Lu}/u.test(previous) &&
        /^\p{Lu}/u.test(last) &&
        !NON_NAME_WORDS.has(previous.toLowerCase())
    ) {
        return `${previous} ${last}`;
    }
    // With a pill present, "and"/a comma also count as person context.
    const listContext =
        previous !== undefined &&
        /\[\[[^:\]]+:[^\]]+\]\]/.test(draft) &&
        (previous.toLowerCase() === 'and' || previous.endsWith(','));
    // A capitalized word can stand alone; a lowercase one needs person context.
    if (
        !/^\p{Lu}/u.test(last) &&
        !listContext &&
        (previous === undefined ||
            !PERSON_CONTEXT_WORDS.has(previous.toLowerCase()))
    ) {
        return null;
    }
    return last;
}

function nameMatchesCandidate(name: string, candidateLower: string): boolean {
    const lower = name.toLowerCase();
    return (
        lower === candidateLower ||
        lower.startsWith(candidateLower) ||
        lower.split(/\s+/u).includes(candidateLower)
    );
}

/**
 * Resolve a name candidate against org caregivers and clients. Returns the
 * matches (one → offer @, several → disambiguate) or null when nothing matches.
 */
export function matchNameCatch(
    candidate: string,
    caregivers: ReadonlyArray<CaregiverLike>,
    clients: ReadonlyArray<ClientLike>,
): NameCatchHint | null {
    const candidateLower = candidate.trim().toLowerCase();
    if (candidateLower.length < 3) {
        return null;
    }
    const matches: Array<NameCatchMatch> = [];
    for (const caregiver of caregivers) {
        if (nameMatchesCandidate(caregiver.name, candidateLower)) {
            matches.push({
                type: 'caregiver',
                id: caregiver.id,
                displayName: caregiver.name,
            });
        }
    }
    for (const client of clients) {
        const full = `${client.first_name} ${client.last_name}`.trim();
        const preferred = client.preferred_name ?? '';
        if (
            nameMatchesCandidate(full, candidateLower) ||
            (preferred !== '' &&
                nameMatchesCandidate(preferred, candidateLower))
        ) {
            matches.push({ type: 'client', id: client.id, displayName: full });
        }
    }
    if (matches.length === 0) {
        return null;
    }
    return { candidate, matches: matches.slice(0, 5) };
}
